import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService, BaseComponent } from '@app/core';
import { AlertDialogComponent } from '@app/dialogs/alert-dialog/alert-dialog.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { catchError } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent extends BaseComponent implements OnInit {
  isLoading = false;
  message!: string;

  resetPasswordToken!: string;
  resetPasswordHash!: string;

  resetPasswordForm!: UntypedFormGroup;
  password_change_hint!: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) {
    super();
    this.createResetPasswordForm();
  }

  ngOnInit(): void {
    this.addSubscription(this.route.params).subscribe((params) => {
      if (params) {
        this.resetPasswordToken = params['token'];
        this.resetPasswordHash = params['hash'];
      }
    });
  }

  resetPassword() {
    this.isLoading = true;
    this.addSubscription(
      this.authenticationService.resetPassword({
        ...this.resetPasswordForm.value,
        hash: this.resetPasswordHash,
        token: this.resetPasswordToken,
      }),
    )
      .pipe(
        catchError((err) => {
          this.showError(err.message);
          throw err;
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.isLoading = false;
          this.message = res.data.message;
        }
      });
  }

  backToLogin() {
    this.router.navigate(
      [this.route.snapshot.queryParams['redirect'] || '/login'],
      { replaceUrl: true },
    );
  }

  private createResetPasswordForm() {
    this.resetPasswordForm = this.formBuilder.group({
      password: ['', Validators.required],
      repeat_password: ['', Validators.required],
    });

    this.addSubscription(
      this.resetPasswordForm?.get('password')!.valueChanges,
    ).subscribe((change) => {
      this.checkPassword(change, this.resetPasswordForm.value.repeat_password);
    });

    this.addSubscription(
      this.resetPasswordForm?.get('repeat_password')!.valueChanges,
    ).subscribe((change) => {
      this.checkPassword(this.resetPasswordForm.value.password, change);
    });
  }

  checkPassword(p: string, pNew: string) {
    const uppercasePattern = /[A-Z]/;
    const lowercasePattern = /[a-z]/;
    const numberPattern = /[0-9]/;
    const symbolPattern = /[!@#$%^&*(),.?":{}|<>]/;

    if (p.length == 0 || pNew.length == 0) {
      this.password_change_hint = '';
      return;
    }

    if (pNew.length >= 8) {
      if (!uppercasePattern.test(p) && !uppercasePattern.test(pNew)) {
        this.password_change_hint = this.translateService.instant(
          'password_requires_uppercase',
        );
        return;
      }

      if (!lowercasePattern.test(p) && !lowercasePattern.test(pNew)) {
        this.password_change_hint = this.translateService.instant(
          'password_requires_lowercase',
        );
        return;
      }

      if (!numberPattern.test(p) && !numberPattern.test(pNew)) {
        this.password_change_hint = this.translateService.instant(
          'password_requires_number',
        );
        return;
      }

      if (!symbolPattern.test(p) && !symbolPattern.test(pNew)) {
        this.password_change_hint = this.translateService.instant(
          'password_requires_symbol',
        );
        return;
      }

      if (p !== pNew) {
        this.password_change_hint = this.translateService.instant(
          'passwords_not_identical',
        );
      } else {
        this.password_change_hint = '';
      }
    } else {
      this.password_change_hint = this.translateService.instant(
        'password_min_length',
      );
    }
  }

  showError(errorMessage: string) {
    this.dialog.open(AlertDialogComponent, {
      data: {
        title: this.translateService.instant('reset_password_error'),
        body: errorMessage,
        actions: [
          {
            index: 'ok',
            label: 'ok',
          },
        ],
      },
    });
  }
}
